/**
 * Comprehensive Spacing System
 * Provides semantic spacing values with responsive scaling
 * for consistent layout across all dashboard components
 */

// Base spacing unit (8px)
const BASE_UNIT = 8;

// Spacing scale multipliers
const SPACING_SCALE = {
  xs: 0.5,    // 4px
  sm: 1,      // 8px
  md: 2,      // 16px
  lg: 3,      // 24px
  xl: 4,      // 32px
  xxl: 6,     // 48px
  xxxl: 8,    // 64px
};

// Generate spacing values
const generateSpacing = (multiplier) => `${BASE_UNIT * multiplier}px`;

// Core spacing values
export const spacing = {
  // Basic spacing
  xs: generateSpacing(SPACING_SCALE.xs),
  sm: generateSpacing(SPACING_SCALE.sm),
  md: generateSpacing(SPACING_SCALE.md),
  lg: generateSpacing(SPACING_SCALE.lg),
  xl: generateSpacing(SPACING_SCALE.xl),
  xxl: generateSpacing(SPACING_SCALE.xxl),
  xxxl: generateSpacing(SPACING_SCALE.xxxl),

  // Semantic spacing for specific use cases
  component: {
    // Padding inside components
    padding: {
      xs: generateSpacing(1),     // 8px - tight padding
      sm: generateSpacing(1.5),   // 12px - small padding
      md: generateSpacing(2),     // 16px - standard padding
      lg: generateSpacing(3),     // 24px - comfortable padding
      xl: generateSpacing(4),     // 32px - spacious padding
    },
    
    // Margins between components
    margin: {
      xs: generateSpacing(0.5),   // 4px - minimal margin
      sm: generateSpacing(1),     // 8px - small margin
      md: generateSpacing(2),     // 16px - standard margin
      lg: generateSpacing(3),     // 24px - comfortable margin
      xl: generateSpacing(4),     // 32px - large margin
      xxl: generateSpacing(6),    // 48px - section margin
    },
    
    // Gaps in flex/grid layouts
    gap: {
      xs: generateSpacing(0.5),   // 4px - tight gap
      sm: generateSpacing(1),     // 8px - small gap
      md: generateSpacing(2),     // 16px - standard gap
      lg: generateSpacing(3),     // 24px - comfortable gap
      xl: generateSpacing(4),     // 32px - large gap
    }
  },

  // Layout-specific spacing
  layout: {
    // Container padding
    container: {
      xs: generateSpacing(2),     // 16px - mobile container
      sm: generateSpacing(3),     // 24px - tablet container
      md: generateSpacing(4),     // 32px - desktop container
      lg: generateSpacing(6),     // 48px - large desktop container
    },
    
    // Section spacing
    section: {
      xs: generateSpacing(4),     // 32px - small section
      sm: generateSpacing(6),     // 48px - medium section
      md: generateSpacing(8),     // 64px - large section
      lg: generateSpacing(12),    // 96px - extra large section
    },
    
    // Header/footer spacing
    header: {
      height: generateSpacing(8), // 64px - header height
      padding: generateSpacing(2), // 16px - header padding
    },
    
    // Sidebar spacing
    sidebar: {
      width: generateSpacing(32), // 256px - sidebar width
      padding: generateSpacing(2), // 16px - sidebar padding
    }
  },

  // Dashboard-specific spacing
  dashboard: {
    // Card spacing
    card: {
      padding: generateSpacing(3),    // 24px - card padding
      margin: generateSpacing(2),     // 16px - card margin
      gap: generateSpacing(2),        // 16px - gap between cards
    },
    
    // Chart spacing
    chart: {
      padding: generateSpacing(2),    // 16px - chart padding
      margin: generateSpacing(1.5),   // 12px - chart margin
      legend: generateSpacing(1),     // 8px - legend spacing
    },
    
    // Table spacing
    table: {
      cellPadding: generateSpacing(1.5), // 12px - table cell padding
      rowGap: generateSpacing(0.5),      // 4px - row gap
      headerPadding: generateSpacing(2), // 16px - header padding
    },
    
    // Tab spacing
    tab: {
      padding: generateSpacing(2),    // 16px - tab padding
      margin: generateSpacing(1),     // 8px - tab margin
      content: generateSpacing(3),    // 24px - tab content padding
    }
  }
};

// Responsive spacing utilities
export const responsiveSpacing = {
  // Generate responsive spacing based on breakpoints
  responsive: (xsValue, smValue, mdValue, lgValue) => ({
    xs: generateSpacing(xsValue),
    sm: generateSpacing(smValue || xsValue),
    md: generateSpacing(mdValue || smValue || xsValue),
    lg: generateSpacing(lgValue || mdValue || smValue || xsValue),
  }),
  
  // Predefined responsive patterns
  patterns: {
    // Container padding that scales with screen size
    containerPadding: {
      xs: spacing.layout.container.xs,
      sm: spacing.layout.container.sm,
      md: spacing.layout.container.md,
      lg: spacing.layout.container.lg,
    },
    
    // Section margins that scale with screen size
    sectionMargin: {
      xs: spacing.layout.section.xs,
      sm: spacing.layout.section.sm,
      md: spacing.layout.section.md,
      lg: spacing.layout.section.lg,
    },
    
    // Component gaps that scale with screen size
    componentGap: {
      xs: spacing.component.gap.sm,
      sm: spacing.component.gap.md,
      md: spacing.component.gap.lg,
      lg: spacing.component.gap.xl,
    }
  }
};

// Utility functions for dynamic spacing
export const spacingUtils = {
  // Multiply base spacing by a factor
  multiply: (factor) => generateSpacing(factor),
  
  // Add two spacing values
  add: (value1, value2) => {
    const num1 = parseInt(value1);
    const num2 = parseInt(value2);
    return `${num1 + num2}px`;
  },
  
  // Get spacing for specific breakpoint
  getBreakpointSpacing: (spacingObj, breakpoint) => {
    return spacingObj[breakpoint] || spacingObj.xs || spacingObj;
  },
  
  // Generate CSS custom properties for spacing
  toCSSCustomProperties: () => {
    const cssProps = {};
    
    // Basic spacing
    Object.entries(spacing).forEach(([key, value]) => {
      if (typeof value === 'string') {
        cssProps[`--spacing-${key}`] = value;
      }
    });
    
    // Component spacing
    Object.entries(spacing.component).forEach(([category, values]) => {
      Object.entries(values).forEach(([size, value]) => {
        cssProps[`--spacing-${category}-${size}`] = value;
      });
    });
    
    // Layout spacing
    Object.entries(spacing.layout).forEach(([category, values]) => {
      Object.entries(values).forEach(([size, value]) => {
        cssProps[`--spacing-${category}-${size}`] = value;
      });
    });
    
    // Dashboard spacing
    Object.entries(spacing.dashboard).forEach(([category, values]) => {
      Object.entries(values).forEach(([size, value]) => {
        cssProps[`--spacing-dashboard-${category}-${size}`] = value;
      });
    });
    
    return cssProps;
  }
};

// Material-UI theme integration
export const muiSpacingOverrides = {
  // Override MUI spacing function
  spacing: (factor) => generateSpacing(factor),
  
  // Component-specific overrides
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          padding: spacing.dashboard.card.padding,
          margin: spacing.dashboard.card.margin,
        }
      }
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: spacing.layout.container.md,
          paddingRight: spacing.layout.container.md,
          '@media (max-width: 600px)': {
            paddingLeft: spacing.layout.container.xs,
            paddingRight: spacing.layout.container.xs,
          },
          '@media (min-width: 1200px)': {
            paddingLeft: spacing.layout.container.lg,
            paddingRight: spacing.layout.container.lg,
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: spacing.dashboard.table.cellPadding,
        },
        head: {
          padding: spacing.dashboard.table.headerPadding,
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: spacing.dashboard.tab.padding,
          margin: spacing.dashboard.tab.margin,
        }
      }
    }
  }
};

export default spacing; 